import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Reset email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setResetComplete(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Email sent confirmation
  if (emailSent) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Check Your Email</h1>
            <p className="text-[#94A3B8] mb-6">
              We've sent a password reset link to <strong className="text-white">{email}</strong>
            </p>
            <p className="text-[#94A3B8] text-sm mb-6">
              The link expires in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#00AEEF] hover:text-[#00FFE0]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Password reset complete
  if (resetComplete) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Password Reset Complete</h1>
            <p className="text-[#94A3B8] mb-6">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to="/student/login"
                className="bg-[#00AEEF] text-white font-bold rounded-xl px-6 py-3 hover:bg-[#00AEEF]/80 transition-all"
              >
                Student Login
              </Link>
              <Link
                to="/parent/login"
                className="bg-[#00FFE0] text-[#0A1F44] font-bold rounded-xl px-6 py-3 hover:bg-[#00FFE0]/80 transition-all"
              >
                Parent Login
              </Link>
              <Link
                to="/teacher/login"
                className="bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 hover:bg-[#FF6A00]/80 transition-all"
              >
                Teacher Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#00AEEF]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {step === 'request' ? 'Forgot Password?' : 'Create New Password'}
            </h1>
            <p className="text-[#94A3B8]">
              {step === 'request' 
                ? "Enter your email and we'll send you a reset link"
                : 'Enter your new password below'
              }
            </p>
          </div>

          {step === 'request' ? (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="reset-email-input"
                    className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF] rounded-xl pl-12 pr-4 py-3 w-full transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF] rounded-xl pl-12 pr-4 py-3 w-full transition-all"
                    placeholder="Min 6 characters"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#94A3B8] mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                    className="bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#00AEEF] focus:ring-1 focus:ring-[#00AEEF] rounded-xl pl-12 pr-4 py-3 w-full transition-all"
                    placeholder="Confirm password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-btn"
                className="w-full bg-[#00FFE0] text-[#0A1F44] font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(0,255,224,0.3)] hover:shadow-[0_0_30px_rgba(0,255,224,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
